import { Injectable } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, defer, from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils, SUSPENDED_EXEMPT_ROUTES } from 'app/core/auth/auth.utils';
import { Router } from '@angular/router';
import { AuthzToastService } from 'app/core/auth/authz-toast.service';
import { environment } from 'environments/environment';

// SPEC-web-planner-007 AC-18 (B12) — user-visible texts per backend error code.
export const FORBIDDEN_MESSAGES: Readonly<Record<string, string>> = {
    not_a_member: 'Not a member of this workspace',
    not_permitted: 'Not permitted for your role',
};
/** Shown for every other 403 (empty body, gateway 403, unknown code): a 403 is never silent. */
export const FORBIDDEN_FALLBACK = 'Not permitted for your role';
/**
 * Domain 403 codes callers already handle themselves, so no generic toast.
 * agents-api 403 codes callers handle (scanned in awvent-backend Agents and meet-UX):
 *  - `not_host`  (RecordRepDecision; agent.service.ts, Qualification review buttons)
 * `not_owner` (Agent/Handled Send/Dismiss) is deliberately NOT listed: home.component
 * shows the raw code and swallows the error, so suppressing it would make the 403 silent.
 * `not_host` is kept because its caller (Qualification review) toasts a message for any 403.
 * Other agents-api 403s (transcript finalize for a non-host) have no meet-UX
 * caller that handles them. Add a code here, or set SKIP_AUTHZ_TOAST on the
 * request, when a caller owns a 403.
 */
export const HANDLED_DOMAIN_403_CODES: ReadonlyArray<string> = ['not_host'];
export const BAD_REQUEST_MESSAGES: Readonly<Record<string, string>> = {
    workspace_required: 'Select a workspace to continue',
    workspace_mismatch: 'That request does not match your selected workspace',
    workspace_invalid: 'Your selected workspace is not valid',
};
export const UNAVAILABLE_MESSAGES: Readonly<Record<string, string>> = {
    identity_unavailable: 'We could not verify your identity right now. Please try again in a moment',
};

/**
 * SPEC-web-planner-013 AC-26s (GAP-BACKEND-417 UI half): under Enforce, every
 * call for a suspended client answers 403 with `erros[0].message` =
 * `client_suspended`. meet-UX treats that as the Suspended status.
 */
export const CLIENT_SUSPENDED = 'client_suspended';
export const SUSPENDED_HOLDING_URL = '/workspace-status?state=suspended';
/** Shown instead of a redirect where the page must keep working (exempt pages): never silent. */
export const SUSPENDED_MESSAGE = "This organisation's access is suspended";
/**
 * Pages where a client_suspended 403 must not redirect: the suspended holding
 * page itself, choosing another client, and the sign-in flows (sign in/out,
 * activation, password reset, unlock, no-workspace page).
 */
const SUSPENDED_REDIRECT_EXEMPT = SUSPENDED_EXEMPT_ROUTES;
const isSuspendedExempt = (url: string): boolean =>
    url.startsWith(SUSPENDED_HOLDING_URL)
    || SUSPENDED_REDIRECT_EXEMPT.some((p) => url === p || url.startsWith(p + '?') || url.startsWith(p + '/'));

/**
 * SPEC-web-planner-024 AC-11 (amended): a 401 from the users-api's anonymous `POST /User/Activate` that carries an
 * Invitation* refusal code (InvitationTokenMismatch) is for /activate to show, not a session end.
 */
export function isInviteActivationRefusal(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean
{
    if ( req.method.toUpperCase() !== 'POST' || !USERS_API ) { return false; }
    const lower = req.url.toLowerCase();
    if ( !lower.startsWith(USERS_API) ) { return false; }
    const rest = lower.slice(USERS_API.length);
    if ( rest !== '' && rest[0] !== '/' && rest[0] !== '?' ) { return false; } // only starts like the users-api host
    const path = rest.split('?')[0].replace(/^\/+|\/+$/g, '');
    if ( path !== 'user/activate' ) { return false; }
    let body: any = error.error;
    if ( typeof body === 'string' ) { try { body = JSON.parse(body); } catch { return false; } }
    const code = body?.erros?.[0]?.code ?? body?.Erros?.[0]?.Code;
    return typeof code === 'string' && /^Invitation/.test(code);
}

/** The URL's path with every id-like segment (number, GUID, long token with a digit) replaced by `:id`. */
export function redactedPath(url: string): string
{
    let path = url;
    try { path = new URL(url, 'http://localhost').pathname; } catch { path = url.split('?')[0]; }
    return path.split('/').map((s) =>
        /^\d+$/.test(s) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) || (s.length >= 16 && /\d/.test(s)) ? ':id' : s
    ).join('/');
}

export function isClientSuspendedError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 403 && backendErrorCode(error) === CLIENT_SUSPENDED;
}

/**
 * Set `context: new HttpContext().set(SKIP_AUTHZ_TOAST, true)` on a request whose caller handles 403/400/503 itself.
 * It turns off the toast only: a 403 client_suspended for the selected client still opens the
 * suspended holding page (SPEC-web-planner-013 AC-26s), whoever made the call.
 */
export const SKIP_AUTHZ_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * SPEC-web-planner-013 AC-53 (rev 15c, GAP-MEET-UX-274): the users-api writes the generated
 * client cannot name a client for (`userPut`/`userPost` have no header parameter,
 * `userSendEmailInvitationPut` makes it optional) carry the selected client - an Admin's
 * edit of another person needs it under Enforce; a person's own edit inside the selected
 * client is unaffected (sign-in clears the stored client before its own self-update). An
 * opt-in allowlist, not a global default: a client header on person-level routes moves
 * data between partitions (e.g. NotificationPreferences). An explicit `x-tenant-id` or
 * `x-workspace-id` always wins; `context: new HttpContext().set(NO_TENANT_HEADER, true)`
 * opts a call out.
 */
export const NO_TENANT_HEADER = new HttpContextToken<boolean>(() => false);

/** "METHOD path" after the users-api host (UserController has no api/v1 prefix, rev 15b). */
const CLIENT_WRITES: ReadonlySet<string> = new Set(['PUT user', 'POST user', 'PUT user/sendemailinvitation']);

/** The users-api origin, computed once; a URL must continue with '/', '?' or end after it. */
const USERS_API = environment.api.users.replace(/\/+$/, '').toLowerCase();

/** True when this request is one of the users-api writes that take the selected client. */
export function takesSelectedClient(method: string, url: string): boolean
{
    const lower = url.toLowerCase();
    if ( !USERS_API || !lower.startsWith(USERS_API) )
    {
        return false;
    }
    const rest = lower.slice(USERS_API.length);
    if ( rest !== '' && rest[0] !== '/' && rest[0] !== '?' )
    {
        return false; // only starts like the users-api host
    }
    const path = rest.split('?')[0].replace(/^\/+|\/+$/g, '');
    return CLIENT_WRITES.has(`${method.toUpperCase()} ${path}`);
}

const codeFromBody = (body: any): string | undefined => {
    const code = body?.erros?.[0]?.message ?? body?.Erros?.[0]?.message;
    return typeof code === 'string' ? code.trim().toLowerCase() : undefined;
};

/** Synchronous code extraction for an already-parsed object or a JSON string body. */
export function backendErrorCode(error: HttpErrorResponse): string | undefined {
    let body: any = error.error;
    if ( typeof body === 'string' )
    {
        try { body = JSON.parse(body); } catch { return undefined; }
    }
    return codeFromBody(body);
}

/**
 * SPEC-web-planner-013 AC-54: the text to show for a refusal the caller reports itself
 * (it set SKIP_AUTHZ_TOAST). A header/infrastructure code (workspace_*, identity_unavailable)
 * keeps the interceptor's wording, since the server's text there is for developers; then the
 * server's own sentence (message, erros[]/Erros[], or a ModelState body {"101": ["..."]});
 * then a known 403 code's wording; else the fallback. A string body is parsed as JSON.
 */
export function backendErrorMessage(body: any, fallback: string): string
{
    if ( typeof body === 'string' )
    {
        try { body = JSON.parse(body); } catch { return fallback; }
    }
    if ( !body || typeof body !== 'object' )
    {
        return fallback;
    }
    const code = codeFromBody(body);
    const infra = code ? (BAD_REQUEST_MESSAGES[code] ?? UNAVAILABLE_MESSAGES[code]) : undefined;
    if ( infra )
    {
        return infra;
    }
    const sentence = (v: unknown): string => typeof v === 'string' && v.trim().includes(' ') ? v.trim() : '';
    const candidates: unknown[] = [body.message, body.Message];
    for ( const e of [...(Array.isArray(body.erros) ? body.erros : []), ...(Array.isArray(body.Erros) ? body.Erros : [])] )
    {
        candidates.push(e?.message, e?.Message);
    }
    for ( const [key, value] of Object.entries(body) )
    {
        if ( Array.isArray(value) ) candidates.push(...value); // ModelState: {"101": ["User not found"]}
        // AC-54b: a bare string under a numeric key ({"101": "..."}); not e.g. an ASP.NET `title`.
        else if ( typeof value === 'string' && /^\d+$/.test(key) ) candidates.push(value);
    }
    for ( const c of candidates )
    {
        if ( sentence(c) ) return sentence(c);
    }
    return (code && FORBIDDEN_MESSAGES[code]) || fallback;
}

/** Read a Blob as text: Blob.text() when present, FileReader otherwise (old Safari / WebViews). */
export function readBlobText(blob: Blob): Observable<string> {
    return defer(() => {
        if ( typeof (blob as any).text === 'function' )
        {
            return from((blob as any).text() as Promise<string>);
        }
        return new Observable<string>((subscriber) => {
            const reader = new FileReader();
            reader.onload = () => { subscriber.next(String(reader.result ?? '')); subscriber.complete(); };
            reader.onerror = () => subscriber.error(reader.error);
            reader.readAsText(blob);
        });
    });
}

/** Like backendErrorCode, but also reads a Blob body (responseType 'blob'). Never throws. */
export function backendErrorCode$(error: HttpErrorResponse): Observable<string | undefined> {
    return defer(() => {
        const body: any = error.error;
        if ( typeof Blob !== 'undefined' && body instanceof Blob )
        {
            return readBlobText(body).pipe(map((text) => backendErrorCode(new HttpErrorResponse({ error: text }))));
        }
        return of(backendErrorCode(error));
    }).pipe(catchError(() => of(undefined)));
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    /**
     * Constructor
     */
    constructor(private _authService: AuthService,
        private _router: Router,
        private _toastService: AuthzToastService)
    {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        // SPEC-web-planner-027 AC-2: an expired (or nearly expired) stored token is refreshed before the request. Nothing
        // is refreshed without a stored token and an app session; a failed refresh sends the request as before and the
        // 401 path below applies. Emits synchronously when no refresh is needed.
        return this._authService.refreshIfNeeded().pipe(switchMap(() => this._send(req, next)));
    }

    private _send(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        // Clone the request object
        let headers = req.headers;

        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if ( this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken) )
        {
            headers = headers.set('Authorization', 'Bearer ' + this._authService.accessToken);
        }

        // AC-53: the Admin's users-api writes carry the selected client (allowlist above).
        if ( !headers.has('x-tenant-id') && !headers.has('x-workspace-id') && !req.context.get(NO_TENANT_HEADER)
            && takesSelectedClient(req.method, req.url) )
        {
            let selected: string | undefined;
            try { selected = this._authService.selectedTenant?.id; }
            catch ( e ) { console.warn('[auth] the stored selected client could not be read; x-tenant-id not added', e); }
            if ( selected )
            {
                headers = headers.set('x-tenant-id', selected);
            }
        }
        const newReq = req.clone({ headers });

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {

                // Catch "401 Unauthorized" responses. SPEC-web-planner-024 AC-11 (amended): the signed-out
                // invite activation answers 401 InvitationTokenMismatch, a refusal /activate shows, not a session end.
                if ( error instanceof HttpErrorResponse && error.status === 401 && !isInviteActivationRefusal(req, error) )
                {
                    // SPEC-web-planner-027 AC-3: a session that ends says why (`?reason=expired`). No session to end
                    // (signed-out page, or the user already chose to sign out): no reason.
                    const hadSession = this._authService.hasAppSession;
                    this._warnSessionEnd(req);
                    // Sign out
                    this._authService.signOut().finally(() => {
                        if ( hadSession )
                        {
                            this._router.navigate(['/sign-in'], { queryParams: { reason: 'expired' } });
                        }
                        else
                        {
                            this._router.navigate(['/sign-in']);
                        }
                    });
                    // Reload the app
                    // location.reload();
                }

                if ( !(error instanceof HttpErrorResponse) || ![400, 403, 503].includes(error.status) )
                {
                    return throwError(() => error);
                }
                const skipToast = req.context.get(SKIP_AUTHZ_TOAST);
                // Only a 403 can be client_suspended; otherwise a skipped toast needs no body read.
                if ( skipToast && error.status !== 403 )
                {
                    return throwError(() => error);
                }

                // Read the code (a Blob body is async), act on it, then rethrow the
                // original error unchanged. Nothing here may replace it.
                return backendErrorCode$(error).pipe(
                    map((code) => {
                        // AC-26s: a suspended client goes to its holding page, not a
                        // role toast, whoever made the call.
                        if ( error.status === 403 && code === CLIENT_SUSPENDED )
                        {
                            try
                            {
                                const outcome = this._toSuspendedHoldingPage(newReq);
                                if ( (outcome === 'exempt' || outcome === 'no-client-named') && !skipToast )
                                {
                                    this._toastService.show(SUSPENDED_MESSAGE);
                                }
                            }
                            catch { /* never replace the HTTP error */ }
                            return;
                        }
                        if ( skipToast )
                        {
                            return;
                        }
                        try
                        {
                            const message = this._userMessageFor(error.status, code);
                            if ( message )
                            {
                                this._toastService.show(message);
                            }
                        }
                        catch { /* a toast failure must never replace the HTTP error */ }
                    }),
                    switchMap(() => throwError(() => error))
                );
            })
        );
    }

    /**
     * SPEC-web-planner-027 AC-3: one console.warn per 401 sign-out with the stored token's age in minutes and the
     * request path (ids replaced by `:id`), to settle the 25-30 minute bounce (12.1b) on DEV. Off in production.
     */
    private _warnSessionEnd(req: HttpRequest<any>): void
    {
        if ( environment.production ) { return; }
        try
        {
            const age = AuthUtils.tokenAgeMinutes(this._authService.accessToken);
            console.warn(`[auth] 401 sign-out: token age ${age === null ? 'unknown' : age + ' min'}, path ${redactedPath(req.url)}`);
        }
        catch { /* logging must never change the sign-out */ }
    }

    /**
     * AC-26s: send the person to the suspended holding page, unless the reply is
     * for a client other than the one now selected (a late answer after switching),
     * or they are on a page that must keep working (holding page, choosing another
     * client, signing in or out).
     */
    private _toSuspendedHoldingPage(req: HttpRequest<any>): 'redirected' | 'exempt' | 'not-this-client' | 'no-client-named' | 'on-holding-page'
    {
        // Nobody signed in to a client any more (signed out), or the reply is
        // for another client than the selected one (a late answer after
        // switching): nothing to send them to, and nothing to say.
        const selected = this._authService.selectedTenant?.id;
        // x-workspace-id is the OD-9 alias of x-tenant-id (the backend reads it when x-tenant-id is absent).
        const tenantId = req.headers.get('x-tenant-id') ?? req.headers.get('x-workspace-id');
        if ( !selected || (tenantId && tenantId !== selected) )
        {
            return 'not-this-client';
        }
        // While a navigation runs, router.url is still the page being left:
        // the destination decides.
        const target = this._router.getCurrentNavigation?.()?.extractedUrl?.toString() ?? '';
        const where = target || (this._router.url ?? '');
        // Already on (or on the way to) the holding page, e.g. the other calls
        // of a page whose first 403 started the redirect: nothing more to say.
        if ( where.startsWith(SUSPENDED_HOLDING_URL) )
        {
            return 'on-holding-page';
        }
        // A call that names no client (e.g. GET /Tenant/{id} from tenant
        // selection) may concern any client: say it, do not redirect.
        if ( !tenantId )
        {
            return 'no-client-named';
        }
        if ( isSuspendedExempt(where) )
        {
            return 'exempt';
        }
        this._router.navigateByUrl(SUSPENDED_HOLDING_URL);
        return 'redirected';
    }

    /**
     * AC-18: message for a 403, a 400 workspace_* and a 503 identity_unavailable.
     * A 403 is never silent: not_a_member / not_permitted get their own text and any
     * other 403 gets the generic one, except the domain codes callers handle
     * (HANDLED_DOMAIN_403_CODES). 401 is handled above.
     */
    private _userMessageFor(status: number, code: string | undefined): string | undefined
    {
        const own = (table: Readonly<Record<string, string>>): string | undefined =>
            // own-property check: a code such as "constructor" must not resolve to Object.prototype members
            code && Object.prototype.hasOwnProperty.call(table, code) ? table[code] : undefined;
        switch ( status )
        {
            case 403:
                if ( code && HANDLED_DOMAIN_403_CODES.includes(code) ) { return undefined; }
                return own(FORBIDDEN_MESSAGES) ?? FORBIDDEN_FALLBACK;
            case 400: return own(BAD_REQUEST_MESSAGES);
            case 503: return own(UNAVAILABLE_MESSAGES);
            default: return undefined;
        }
    }
}
